import { type FC, useContext, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useUpdateNodeInternals } from '@xyflow/react';
import {
  FormDatePicker,
  type FormFieldProps,
  FormInput,
  FormInputNumber,
  FormSelect,
  useAutoClearFormFieldValueIfNotInOptions,
} from '../../../../../common/form';
import type { UUIDType } from '../../../../../common/utils';
import { useAllClassifierCategoriesQuery } from '../../../../../classifier';
import { useAlgorithmSourceDimensionCategoriesQuery } from '../../../joins/query/useDimensionCategoriesQuery';
import {
  isDimensionWithCategories,
  useIsDimensionWithDataType,
} from '../../../joins/utils';
import type { AlgorithmSourceDimensionFormDataType } from '../../../sources/schema/types';
import type {
  AlgorithmJoinsAndMappingsGraphNodeOfType,
  AlgorithmJoinsAndMappingsGraphValidFormDataType,
} from '../../schema/types';
import {
  type AlgorithmJoinHandleId,
  decodeDimensionCodeValue,
  parseHandleId,
} from '../../utils';
import { AlgorithmJoinsWithMappingsGraphContext } from '../AlgorithmJoinsWithMappingsGraphContext';
import { useConnection } from '../useConnection';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './ConstantFormField.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'constant-form-field',
);
addTranslations(translations);

type ConstantFormFieldBaseProps = Omit<
  FormFieldProps,
  'name' | 'children' | 'defaultValue'
> & {
  name:
    | `nodes.${number}.data.constantValue`
    | `nodes.${number}.data.items.${number}.constantValue`;
};

export interface ConstantFormFieldProps extends ConstantFormFieldBaseProps {
  type: 'source' | 'target';
  nodeId: UUIDType;
  itemIndex?: number;
}

export const ConstantFormField: FC<ConstantFormFieldProps> = (props) => {
  const { type, nodeId, itemIndex = 0, ...formFieldProps } = props;
  const { isDbSources } = useContext(AlgorithmJoinsWithMappingsGraphContext);
  const { getValues } =
    useFormContext<AlgorithmJoinsAndMappingsGraphValidFormDataType>();

  const connection = useConnection(nodeId, itemIndex, type);
  const connectedNode = useMemo(() => {
    const connectedNodeId =
      type === 'source' ? connection?.target : connection?.source;
    return connectedNodeId
      ? getValues('nodes').find(({ id }) => id === connectedNodeId)
      : undefined;
  }, [connection, getValues, type]);

  if (isDbSources || !connectedNode) {
    return <ConstantTextFormField {...formFieldProps} />;
  }

  if (connectedNode.type === 'algorithmMapping') {
    const { classifierToCode } = connectedNode.data.algorithmMapping;
    if (!classifierToCode) {
      return <ConstantTextFormField {...formFieldProps} />;
    }
    return (
      <MappingCategoryFormSelect
        {...formFieldProps}
        classifierToCode={classifierToCode}
      />
    );
  }

  if (connectedNode.type !== 'algorithmSource') {
    throw new Error(
      `Expecting connected node to be of type "algorithmSource" but got "${connectedNode.type}"`,
    );
  }

  const { itemIndex: connectedItemIndex } = parseHandleId(
    (type === 'source'
      ? connection.targetHandle
      : connection.sourceHandle) as AlgorithmJoinHandleId,
  );
  const connectedNodeItem = connectedNode.data.items[connectedItemIndex];

  if (!connectedNodeItem || !('dimensionCode' in connectedNodeItem)) {
    throw new Error(
      `Expecting connected node item with "dimensionCode" but got ${JSON.stringify(connectedNodeItem, null, 2)}`,
    );
  }

  return (
    <NonDbConstantOrCategoryField
      itemIndex={itemIndex}
      nodeId={nodeId}
      connectedNode={connectedNode}
      connectedNodeItem={connectedNodeItem}
      {...formFieldProps}
    />
  );
};

const ConstantTextFormField: FC<ConstantFormFieldBaseProps> = (props) => (
  <FormInput className="nodrag" style={{ width: '100%' }} {...props} />
);

interface NonDbConstantOrCategoryFieldProps extends ConstantFormFieldBaseProps {
  itemIndex: number;
  nodeId: UUIDType;
  connectedNode: AlgorithmJoinsAndMappingsGraphNodeOfType<'algorithmSource'>;
  connectedNodeItem: {
    id?: number;
    dimensionCode: string;
  };
}

const NonDbConstantOrCategoryField: FC<NonDbConstantOrCategoryFieldProps> = (
  props,
) => {
  const {
    itemIndex,
    nodeId,
    connectedNode,
    connectedNodeItem,
    name,
    ...formFieldProps
  } = props;
  const { algorithmSourcesById } = useContext(
    AlgorithmJoinsWithMappingsGraphContext,
  );
  const { algorithmSourceId } = connectedNode.data;

  const isDimensionWithNumberDataType = useIsDimensionWithDataType('NUMBER');
  const isDimensionWithDateDataType = useIsDimensionWithDataType('DATE');

  const connectedDimension = useMemo(() => {
    if (!connectedNodeItem.dimensionCode) {
      return undefined;
    }
    const { dimensionCode } = decodeDimensionCodeValue(
      connectedNodeItem.dimensionCode,
    );
    const algorithmSource = algorithmSourcesById.get(algorithmSourceId);
    const dimension = algorithmSource?.dimensions.find(
      ({ code }) => code === dimensionCode,
    );
    return dimension;
  }, [connectedNodeItem, algorithmSourcesById, algorithmSourceId]);

  const connectedDimensionWithCategories = useMemo(() => {
    if (connectedDimension && isDimensionWithCategories(connectedDimension)) {
      return connectedDimension;
    }
  }, [connectedDimension]);

  const connectedDimensionWithNumberDataType = useMemo(() => {
    if (
      connectedDimension &&
      isDimensionWithNumberDataType(connectedDimension)
    ) {
      return connectedDimension;
    }
  }, [connectedDimension, isDimensionWithNumberDataType]);

  const connectedDimensionWithDateDataType = useMemo(() => {
    if (connectedDimension && isDimensionWithDateDataType(connectedDimension)) {
      return connectedDimension;
    }
  }, [connectedDimension, isDimensionWithDateDataType]);

  const updateNodeInternals = useUpdateNodeInternals();
  useEffect(() => {
    updateNodeInternals(nodeId);
  }, [updateNodeInternals, nodeId, connectedDimensionWithCategories]);

  if (connectedDimensionWithNumberDataType) {
    return <FormInputNumber name={name} />;
  }

  if (connectedDimensionWithDateDataType) {
    return <FormDatePicker name={name} />;
  }

  if (connectedDimensionWithCategories) {
    return (
      <DimensionCategoryFormSelect
        {...formFieldProps}
        name={name}
        dimension={connectedDimensionWithCategories}
      />
    );
  }

  return <ConstantTextFormField name={name} {...formFieldProps} />;
};

interface CategoryFormSelectProps extends ConstantFormFieldBaseProps {
  data: { code: string; name: string }[] | undefined;
  isFetching: boolean;
}

const CategoryFormSelect: FC<CategoryFormSelectProps> = (props) => {
  const { data, isFetching, name, ...formFieldProps } = props;
  const { t } = useTranslation();

  const options = useMemo(
    () =>
      (data || []).map(({ code, name }) => ({
        key: code,
        value: code,
        label: `${code} - ${name}`,
      })),
    [data],
  );

  useAutoClearFormFieldValueIfNotInOptions({
    name,
    options,
    isEnabled: !isFetching,
    fallbackValue: '',
  });

  return (
    <FormSelect
      {...formFieldProps}
      name={name}
      options={options}
      loading={isFetching}
      allowClear
      placeholder={t('choose-category')}
      className="nodrag"
    />
  );
};

interface MappingCategoryFormSelectProps extends ConstantFormFieldBaseProps {
  classifierToCode: string; // always a non-empty string by the time this is rendered
}

const MappingCategoryFormSelect: FC<MappingCategoryFormSelectProps> = ({
  classifierToCode,
  ...rest
}) => {
  const { data, isFetching } = useAllClassifierCategoriesQuery(
    { classifierCode: classifierToCode },
    { enabled: Boolean(classifierToCode) },
  );
  return <CategoryFormSelect {...rest} data={data} isFetching={isFetching} />;
};

interface DimensionCategoryFormSelectProps extends ConstantFormFieldBaseProps {
  dimension: AlgorithmSourceDimensionFormDataType;
}

const DimensionCategoryFormSelect: FC<DimensionCategoryFormSelectProps> = ({
  dimension,
  ...rest
}) => {
  const { data, isFetching } =
    useAlgorithmSourceDimensionCategoriesQuery(dimension);
  return <CategoryFormSelect {...rest} data={data} isFetching={isFetching} />;
};
